import React, { useCallback } from 'react'
import { FormProvider, useForm } from 'react-hook-form'

import EditModalFooter from './EditModalFooter'
import type { PatchCruiseItemViewModel, PatchTourItemViewModel } from './models'
import {
  extractEditableEndDateTimeStrings,
  extractEditableStartDateTimeStrings,
  updateCruisePayloadFromViewModel,
  updateTourPayloadFromViewModel,
} from './utils'
import { formOptions } from '../Common/Fields/config'

import LoadingIndicator from 'components/Common/Loading/LoadingIndicator'
import ModalBody from 'LuxKit/Modal/ModalBody'
import ModalContent from 'LuxKit/Modal/ModalContent'
import ModalHeader from 'LuxKit/Modal/ModalHeader'
import CruiseFields from 'tripPlanner/components/TripModal/TripItems/Common/CruiseFields'
import TourFields from 'tripPlanner/components/TripModal/TripItems/Common/TourFields'
import DeleteTripItem from 'tripPlanner/components/TripModal/TripItems/DeleteTripItem'
import { usePatchTripItem } from 'tripPlanner/hooks/api/tripItem'
import useModalCallbacks from 'tripPlanner/hooks/View/useModalCallbacks'
import useOpenModal from 'tripPlanner/hooks/View/useOpenModal'
import type { CruiseItem, TourItem } from 'tripPlanner/types/tripItem'

type ItemType = TourItem | CruiseItem

interface Props {
  item: ItemType
}

type ViewModel = PatchTourItemViewModel | PatchCruiseItemViewModel

const itemToView = (item: ItemType): ViewModel => {
  return {
    type: item.type,
    name: item.name,
    price: item.price,
    url: item.url,
    isBooked: item.isBooked,
    notes: item.notes,
    operatorName: item.operatorName,
    ...extractEditableStartDateTimeStrings(item),
    ...extractEditableEndDateTimeStrings(item),
    startPlace: item.startPlace,
    endPlace: item.endPlace,
    isEndDateSame: false,
  }
}

function EditTourAndCruises(props: Props) {
  const { item } = props

  const openModal = useOpenModal()
  const { closeModal } = useModalCallbacks()

  const methods = useForm<ViewModel>({
    ...formOptions,
    defaultValues: itemToView(item),
  })
  const { handleSubmit } = methods

  const { mutate: updateMutate, isLoading } = usePatchTripItem({
    onSuccess: () => {
      closeModal()
    },
  })

  const deleteItem = useCallback(
    () => openModal(DeleteTripItem, { item }, { height: 'auto' }),
    [openModal, item],
  )

  const updateItem = useCallback(
    ({
      isEndPlaceSame,
      ...viewModel
    }: PatchTourItemViewModel | PatchCruiseItemViewModel) => {
      switch (viewModel.type) {
        case 'TOUR':
          updateMutate({
            tripId: item.tripId,
            tripItemId: item.id,
            tripItem: updateTourPayloadFromViewModel(viewModel),
          })
          break
        case 'CRUISE':
          updateMutate({
            tripId: item.tripId,
            tripItemId: item.id,
            tripItem: updateCruisePayloadFromViewModel(viewModel),
          })
          break
      }
    },
    [item, updateMutate],
  )

  return (
    <>
      <ModalHeader
        title="Edit Tour or Cruise"
        onCloseButtonClick={closeModal}
      />
      <ModalBody>
        <FormProvider {...methods}>
          <ModalContent>
            {item.type === 'TOUR' && <TourFields tripId={item.tripId} />}
            {item.type === 'CRUISE' && <CruiseFields tripId={item.tripId} />}
          </ModalContent>
        </FormProvider>
      </ModalBody>
      <EditModalFooter
        deleteItem={deleteItem}
        onSubmit={handleSubmit(updateItem)}
        isLoading={isLoading}
      />
      {isLoading && <LoadingIndicator floating />}
    </>
  )
}

export default EditTourAndCruises
